import {
  JE_ParsingContext,
  JE_ClaimStore,
  createFQClaim,
  createFQClaimXform,
  createFQTechnicalProfile,
  JE_FQMetadataItemSpec,
  addFQOrchestrationStepToJourney,
  initJEParsingContent,
  generateXMLFromFQSpecs,
  addBranchVarIDToUserJourney,
  addNewUserJourney,
} from './jsonFQContext';
import {
  JsonFlowchartNode,
  JE_FlowchartIndex,
  convertFlowchartDataToGraph,
  JE_SignInNodeData,
} from './flowchartToGraph';
import { linkInfo, getLinkID } from './utils';
import constants from '../constants/jsonXMLConstants';
import settings from '../constants/jsonXMLsettings';
import { ObjectFlags } from 'typescript';

interface JsonAttributePlaceholder {
  attributeId: string;
  type: string; // 'boolean' or 'string'
  displayName: string;
  inputMethod?: string;
  selectValues?: JsonAttributeOptionsPlaceholder[];
}

interface JsonAttributeOptionsPlaceholder {
  text: string;
  value: string | boolean;
}

export interface ConditionalRef {
  nodeId: string;
  conditionIsTrue: boolean;
}

export interface BranchVarRef {
  incidentBranchVars: string[];
  branchVarId?: string;
}

export const generateBranchVar = (parsingCtx: JE_ParsingContext, journeyId: string, metadata: BranchVarMetadata) => {
  let branchVarClaim = createFQClaim(parsingCtx.claimStore, {
    id: `BranchVar_${parsingCtx.allBranchVars.length}`,
    dataType: 'boolean',
    displayName: `Branch Variable ${parsingCtx.allBranchVars.length}`,
  });
  parsingCtx.allBranchVars.push(branchVarClaim.id);
  addBranchVarIDToUserJourney(parsingCtx.userJourneyStore, branchVarClaim.id, journeyId);
  if (settings.outputExtraBranchVarInfo) {
    metadata[branchVarClaim.id] = { ancestors: new Set(), directChildren: new Set(), descendants: new Set(), mutex: new Set(), setInProfiles: new Set() };
  }
  return branchVarClaim.id;
};

export type ConditionalMap = { [index: string]: ConditionalRef[] };

export type BranchVarMap = { [index: string]: BranchVarRef };

export type BranchVarMetadata = { [index: string]: { ancestors: Set<string>, directChildren: Set<string>, descendants: Set<string>, mutex: Set<string>, setInProfiles: Set<string> } };

export const collectAttributeClaims = (
  parsingCtx: JE_ParsingContext,
  attributes: { [index: string]: JsonAttributePlaceholder },
) => {
  Object.keys(attributes).forEach((attrId: string) => {
    let createdClaim = createFQClaim(parsingCtx.claimStore, {
      id: attributes[attrId].attributeId,
      dataType: attributes[attrId].type,
      displayName: attributes[attrId].displayName,
    });
    if (createdClaim.id != attributes[attrId].attributeId) {
      console.warn('Duplicate claim ID found in explicitly-declared attributes - this can lead to unexpected behavior');
    }
  });
};

const populateLinkConditionVars = (
  parsingCtx: JE_ParsingContext,
  nodes: { [index: string]: JsonFlowchartNode },
  signInNode: JsonFlowchartNode,
) => {
  let linkIDs = signInNode.children.map((chNodeId: string) => {
    return getLinkID(nodes[chNodeId]);
  });
  let linkConditionMap: { [id: string]: string } = {};
  linkIDs.forEach((apid: string) => {
    linkConditionMap[apid] = 'jexg_sisu_link_${apid}';
  });
  return linkConditionMap;
};

// Generates a topological ordering for a flowchart and assigns branch variables to its nodes.
// Will recursively apply to flows for submit actions under the provided flowchart.
export const statJsonFlowchart = (
  parsingCtx: JE_ParsingContext,
  rootId: string,
  nodes: { [index: string]: JsonFlowchartNode },
  journeyId: string,
  fcIndex: { [index: string]: JE_FlowchartIndex },
) => {
  // Step 1: Generate a topological ordering over the flowchart nodes, and compute their conditional map.
  let visited: Set<string> = new Set();
  let searchQ: string[] = [rootId];
  let topologicalOrder: string[] = [];
  let conditionalMap: ConditionalMap = {};
  let appliedConditionalMap: ConditionalMap = {};
  let branchVarMap: BranchVarMap = {};
  let tokenIssuerId: string | null = null;
  let allFlowchartBranchVars: Set<string> = new Set();
  while (searchQ.length > 0) {
    let nextNodeId = searchQ.shift()!;
    let nextNode = nodes[nextNodeId];
    // The token issuer has no children and can (and must) always be placed last in the topo ordering.
    if (nextNode.kind === constants.componentTypes.tokenIssuer) {
      tokenIssuerId = nextNodeId;
    } else {
      topologicalOrder.push(nextNodeId);
    }
    visited.add(nextNodeId);
    if (conditionalMap.hasOwnProperty(nextNodeId)) {
      // Eliminate C || !C pairs from map.
      let trueConds: Set<string> = new Set();
      let falseConds: Set<string> = new Set();
      conditionalMap[nextNodeId].forEach((cond: ConditionalRef) => {
        if (cond.conditionIsTrue) {
          trueConds.add(cond.nodeId);
        } else {
          falseConds.add(cond.nodeId);
        }
      });
      let activeConds: ConditionalRef[] = [];
      conditionalMap[nextNodeId].forEach((cond: ConditionalRef) => {
        if (!trueConds.has(cond.nodeId) || !falseConds.has(cond.nodeId)) {
          if (
            !(
              activeConds.filter((cnd) => {
                return cnd.nodeId === cond.nodeId && cond.conditionIsTrue === cnd.conditionIsTrue;
              }).length > 0
            )
          ) {
            activeConds.push(cond);
          }
        }
      });
      if (activeConds.length > 0) {
        appliedConditionalMap[nextNodeId] = activeConds;
      }

      // Pass surviving conditions on to child nodes.
      nextNode.children.forEach((childId: string) => {
        if (!conditionalMap.hasOwnProperty(childId)) {
          conditionalMap[childId] = [];
        }
        conditionalMap[nextNodeId].forEach((cond: ConditionalRef) => {
          conditionalMap[childId].push(cond);
        });
      });
    }
    if (
      nextNode.kind === constants.componentTypes.conditional ||
      nextNode.kind === constants.componentTypes.sessionExists
    ) {
      if (!conditionalMap.hasOwnProperty(nextNode.children[0])) {
        conditionalMap[nextNode.children[0]] = [];
      }
      if (!conditionalMap.hasOwnProperty(nextNode.children[1])) {
        conditionalMap[nextNode.children[1]] = [];
      }
      conditionalMap[nextNode.children[0]].push({ nodeId: nextNodeId, conditionIsTrue: true });
      conditionalMap[nextNode.children[1]].push({ nodeId: nextNodeId, conditionIsTrue: false });
    } else if (nextNode.kind === constants.componentTypes.signIn) {
      if (nextNode.children.length > 1) {
        // Generate conditionals for each child for each link
        let linkConditionMap = populateLinkConditionVars(parsingCtx, nodes, nextNode);
        // TODO: A bit of a kludge for when child nodes may not match link metadata
        let hasLinkChild = false;
        nextNode.children.forEach((childId: string) => {
          if (!conditionalMap.hasOwnProperty(childId)) {
            conditionalMap[childId] = [];
          }
          Object.keys(linkConditionMap).forEach((linkId: string) => {
            if (getLinkID(nodes[childId]) === linkId) {
              if (linkId !== 'localAccount') {
                hasLinkChild = true;
              }
              conditionalMap[childId].push({ nodeId: nextNodeId + `_${linkId}`, conditionIsTrue: true });
            } else {
              conditionalMap[childId].push({ nodeId: nextNodeId + `_${linkId}`, conditionIsTrue: false });
            }
          });
        });
        (nextNode.data as JE_SignInNodeData).generateLandingCEX = hasLinkChild;
      } else {
        if (getLinkID(nodes[nextNode.children[0]]) !== 'localAccount') {
          (nextNode.data as JE_SignInNodeData).generateLandingCEX = true;
        } else {
          (nextNode.data as JE_SignInNodeData).generateLandingCEX = false;
        }
      }
    }
    nextNode.children.forEach((childId: string) => {
      // Only add children to the queue if every parent has been expanded - this will preserve topological ordering
      if (nodes[childId].parents.every((parentId: string) => visited.has(parentId))) {
        searchQ.push(childId);
      }
    });
  }
  if (tokenIssuerId !== null) {
    topologicalOrder.push(tokenIssuerId);
  }
  // Step 2: Assign branch vars.
  topologicalOrder.forEach((nodeId: string) => {
    if (branchVarMap.hasOwnProperty(nodeId) && branchVarMap[nodeId].incidentBranchVars.length > 0) {
      if (branchVarMap[nodeId].incidentBranchVars.length > 1) {
        let newBranchVarId = generateBranchVar(parsingCtx, journeyId, parsingCtx.branchVarMetadata);
        allFlowchartBranchVars.add(newBranchVarId);
        branchVarMap[nodeId].branchVarId = newBranchVarId;
        if (settings.outputExtraBranchVarInfo) {
          branchVarMap[nodeId].incidentBranchVars.forEach((incidentVarId: string) => {
            parsingCtx.branchVarMetadata[incidentVarId].ancestors.forEach((ancestorId: string) => {
              parsingCtx.branchVarMetadata[newBranchVarId].ancestors.add(ancestorId);
              parsingCtx.branchVarMetadata[ancestorId].descendants.add(newBranchVarId);
            });
            parsingCtx.branchVarMetadata[newBranchVarId].ancestors.add(incidentVarId);
            parsingCtx.branchVarMetadata[incidentVarId].directChildren.add(newBranchVarId);
            parsingCtx.branchVarMetadata[incidentVarId].descendants.add(newBranchVarId);
          });
        }
      } else {
        branchVarMap[nodeId].branchVarId = branchVarMap[nodeId].incidentBranchVars[0];
      }
    }
    if (nodes[nodeId].children.length > 1) {
      nodes[nodeId].childBranchVars = [];
      nodes[nodeId].children.forEach((childId: string) => {
        if (appliedConditionalMap.hasOwnProperty(childId) && appliedConditionalMap[childId].length > 0) {
          // Nodes not in the conditional map appear on every branch and have no branch variable
          if (!branchVarMap.hasOwnProperty(childId)) {
            branchVarMap[childId] = {
              incidentBranchVars: [],
            };
          }
          let newBranchVar = generateBranchVar(parsingCtx, journeyId, parsingCtx.branchVarMetadata);
          allFlowchartBranchVars.add(newBranchVar);
          if (settings.outputExtraBranchVarInfo) {
            if (branchVarMap.hasOwnProperty(nodeId) && branchVarMap[nodeId].branchVarId) {
              parsingCtx.branchVarMetadata[branchVarMap[nodeId].branchVarId!].descendants.add(newBranchVar);
              parsingCtx.branchVarMetadata[branchVarMap[nodeId].branchVarId!].directChildren.add(newBranchVar);
              parsingCtx.branchVarMetadata[branchVarMap[nodeId].branchVarId!].ancestors.forEach(ancestorVar => {
                parsingCtx.branchVarMetadata[newBranchVar].ancestors.add(ancestorVar);
                parsingCtx.branchVarMetadata[ancestorVar].descendants.add(newBranchVar);
              });
              parsingCtx.branchVarMetadata[newBranchVar].ancestors.add(branchVarMap[nodeId].branchVarId!);
            } else {
              // If this node is not in the conditional map, it appears on every branch. This means every topologically-preceding
              // branch variable is an ancestor of every branch variable generated for this nodes's children.
              // Don't filter off of allBranchVars because it will include different flowchart's branch variables.
              Array.from(allFlowchartBranchVars).filter(branchVar => (branchVar !== newBranchVar && !nodes[nodeId].childBranchVars.includes(branchVar))).forEach(parentBranchVar => {
                if (parsingCtx.branchVarMetadata[parentBranchVar]) {
                  parsingCtx.branchVarMetadata[parentBranchVar].descendants.add(newBranchVar);
                  parsingCtx.branchVarMetadata[newBranchVar].ancestors.add(parentBranchVar);
                }
              })
            }
          }
          branchVarMap[childId].incidentBranchVars.push(newBranchVar);
          nodes[nodeId].childBranchVars.push(newBranchVar);
        } else {
          nodes[nodeId].childBranchVars.push(null);
        }
      });
    } else if (nodes[nodeId].children.length === 1) {
      if (branchVarMap.hasOwnProperty(nodeId) && branchVarMap[nodeId].branchVarId) {
        let childId = nodes[nodeId].children[0];
        if (appliedConditionalMap.hasOwnProperty(childId) && appliedConditionalMap[childId].length > 0) {
          if (!branchVarMap.hasOwnProperty(childId)) {
            branchVarMap[childId] = {
              incidentBranchVars: [],
            };
          }
          branchVarMap[childId].incidentBranchVars.push(branchVarMap[nodeId].branchVarId!);
        }
      }
    }
  });
  if (settings.outputExtraBranchVarInfo) {
    allFlowchartBranchVars.forEach((branchVar) => {
      Array.from(allFlowchartBranchVars).filter(bv => {
        return (!parsingCtx.branchVarMetadata[branchVar!].ancestors.has(bv)) &&
          (!parsingCtx.branchVarMetadata[branchVar!].descendants.has(bv)) &&
          (bv !== branchVar)
      }).forEach(mutexBranchVar => {
        parsingCtx.branchVarMetadata[branchVar].mutex.add(mutexBranchVar);
      });
    });
  }
  return { topologicalOrder, branchVarMap };
};
